import React, { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../styles/shared';

const SIGNAL_COLORS = { BULLISH: '#00ff88', NEUTRAL: '#f59e0b', BEARISH: '#ef4444' };
const SIGNALS = ['BULLISH', 'NEUTRAL', 'BEARISH'];

const s = {
  page:    { minHeight: '100vh', background: '#0f1923', padding: '32px 24px',
             fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' },
  card:    { background: '#1a2535', border: '1px solid #2d4060', borderRadius: 12, padding: '24px' },
  label:   { fontSize: 12, color: '#64748b', marginBottom: 6, display: 'block' },
  input:   { background: '#0f1923', border: '1px solid #2d4060', borderRadius: 8,
             color: '#f1f5f9', padding: '10px 12px', fontSize: 14, width: '100%', boxSizing: 'border-box' },
  btn:     { border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14,
             fontWeight: 700, cursor: 'pointer' },
  th:      { padding: '10px 12px', textAlign: 'left', fontSize: 12,
             color: '#64748b', borderBottom: '1px solid #2d4060', fontWeight: 500 },
  td:      { padding: '12px', fontSize: 13, color: '#f1f5f9', borderBottom: '1px solid #1e2d3d' }
};

function StatCard({ label, value, color }) {
  return (
    <div style={{ ...s.card, flex: 1, textAlign: 'center', padding: '16px' }}>
      <div style={{ fontSize: 24, fontWeight: 700, color: color || '#f1f5f9' }}>{value}</div>
      <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>{label}</div>
    </div>
  );
}

export default function TradingSignalsPage({ token }) {
  const [trades, setTrades]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [showForm, setShowForm] = useState(false);
  const [closeTarget, setCloseTarget] = useState(null);
  const [form, setForm] = useState({
    ticker: '', signal: 'BULLISH', score: '', entryDate: '', entryPrice: '', notes: ''
  });
  const [closeForm, setCloseForm] = useState({ exitDate: '', exitPrice: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const fetchTrades = useCallback(() => {
    fetch(`${API_URL}/api/trading/signals`, {
      headers: { Authorization: 'Bearer ' + token }
    })
      .then(r => r.json())
      .then(d => { if (d.success) setTrades(d.trades); else setError(d.message); })
      .catch(() => setError('Failed to load trades'))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { fetchTrades(); }, [fetchTrades]);

  const handleAdd = () => {
    if (!form.ticker || !form.entryDate) return;
    setSaving(true);
    fetch(`${API_URL}/api/trading/signals`, {
      method: 'POST',
      headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ticker:     form.ticker.toUpperCase(),
        signal:     form.signal,
        score:      form.score ? Number(form.score) : undefined,
        entryDate:  form.entryDate,
        entryPrice: form.entryPrice ? Number(form.entryPrice) : undefined,
        notes:      form.notes
      })
    })
      .then(r => r.json())
      .then(d => {
        if (d.success) {
          setTrades(prev => [d.trade, ...prev]);
          setForm({ ticker: '', signal: 'BULLISH', score: '', entryDate: '', entryPrice: '', notes: '' });
          setShowForm(false);
        } else setError(d.message);
      })
      .catch(() => setError('Failed to save'))
      .finally(() => setSaving(false));
  };

  const handleClose = (tradeId) => {
    setSaving(true);
    fetch(`${API_URL}/api/trading/signals/${tradeId}`, {
      method: 'PATCH',
      headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        exitDate:  closeForm.exitDate,
        exitPrice: closeForm.exitPrice ? Number(closeForm.exitPrice) : undefined,
        notes:     closeForm.notes
      })
    })
      .then(r => r.json())
      .then(d => {
        if (d.success) {
          setTrades(prev => prev.map(t => t._id === tradeId ? d.trade : t));
          setCloseTarget(null);
          setCloseForm({ exitDate: '', exitPrice: '', notes: '' });
        } else setError(d.message);
      })
      .catch(() => setError('Failed to update'))
      .finally(() => setSaving(false));
  };

  const handleDelete = (tradeId) => {
    if (!window.confirm('Delete this trade log?')) return;
    fetch(`${API_URL}/api/trading/signals/${tradeId}`, {
      method: 'DELETE',
      headers: { Authorization: 'Bearer ' + token }
    }).then(() => setTrades(prev => prev.filter(t => t._id !== tradeId)));
  };

  // Stats
  const closed = trades.filter(t => t.status === 'closed');
  const wins   = closed.filter(t => t.outcome > 0).length;
  const avgOut = closed.length
    ? (closed.reduce((s, t) => s + (t.outcome || 0), 0) / closed.length).toFixed(1)
    : null;
  const winRate = closed.length ? Math.round((wins / closed.length) * 100) : null;

  if (loading) return (
    <div style={s.page}><div style={{ color: '#64748b' }}>Loading…</div></div>
  );

  return (
    <div style={s.page}>
      <div style={{ margin: '0 auto' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: '#f1f5f9', margin: 0 }}>
              Signal Journal
            </h1>
            <div style={{ fontSize: 13, color: '#475569', marginTop: 6 }}>
              Personal record of signals acted on · Super Admin only · Not investment advice
            </div>
          </div>
          <button onClick={() => setShowForm(!showForm)} style={{
            ...s.btn, background: '#00ff88', color: '#0f1923'
          }}>
            + Log Signal
          </button>
        </div>

        {error && (
          <div style={{ background: '#ef444422', border: '1px solid #ef4444', borderRadius: 8,
            padding: '10px 14px', color: '#ef4444', marginBottom: 16, fontSize: 13 }}>
            {error}
          </div>
        )}

        {/* Stats row */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
          <StatCard label="Total Logged"  value={trades.length} />
          <StatCard label="Open"          value={trades.filter(t => t.status === 'open').length}  color="#00d4ff" />
          <StatCard label="Closed"        value={closed.length} />
          <StatCard label="Win Rate"      value={winRate !== null ? winRate + '%' : '—'}
            color={winRate !== null ? (winRate >= 50 ? '#00ff88' : '#ef4444') : '#64748b'} />
          <StatCard label="Avg Outcome"   value={avgOut !== null ? avgOut + '%' : '—'}
            color={avgOut !== null ? (Number(avgOut) >= 0 ? '#00ff88' : '#ef4444') : '#64748b'} />
        </div>

        {/* Add form */}
        {showForm && (
          <div style={{ ...s.card, marginBottom: 24 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#f1f5f9', marginBottom: 18 }}>
              Log a Signal Action
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 14 }}>
              <div>
                <label style={s.label}>Ticker</label>
                <input style={s.input} value={form.ticker} placeholder="AAPL"
                  onChange={e => setForm(p => ({ ...p, ticker: e.target.value.toUpperCase() }))} />
              </div>
              <div>
                <label style={s.label}>Signal</label>
                <select style={s.input} value={form.signal}
                  onChange={e => setForm(p => ({ ...p, signal: e.target.value }))}>
                  {SIGNALS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label style={s.label}>Score (0–100)</label>
                <input style={s.input} type="number" min="0" max="100"
                  value={form.score} placeholder="72"
                  onChange={e => setForm(p => ({ ...p, score: e.target.value }))} />
              </div>
              <div>
                <label style={s.label}>Entry Date</label>
                <input style={s.input} type="date" value={form.entryDate}
                  onChange={e => setForm(p => ({ ...p, entryDate: e.target.value }))} />
              </div>
              <div>
                <label style={s.label}>Entry Price ($)</label>
                <input style={s.input} type="number" step="0.01" placeholder="182.50"
                  value={form.entryPrice}
                  onChange={e => setForm(p => ({ ...p, entryPrice: e.target.value }))} />
              </div>
              <div>
                <label style={s.label}>Notes</label>
                <input style={s.input} placeholder="Why you acted on this signal"
                  value={form.notes}
                  onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={handleAdd} disabled={saving} style={{
                ...s.btn, background: '#00ff88', color: '#0f1923', opacity: saving ? 0.6 : 1
              }}>
                {saving ? 'Saving…' : 'Save Entry'}
              </button>
              <button onClick={() => setShowForm(false)} style={{
                ...s.btn, background: '#1e2d3d', color: '#94a3b8'
              }}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Close trade form (inline) */}
        {closeTarget && (
          <div style={{ ...s.card, marginBottom: 24, border: '1px solid #f59e0b44' }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#f1f5f9', marginBottom: 14 }}>
              Close Trade — {closeTarget.ticker}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 14 }}>
              <div>
                <label style={s.label}>Exit Date</label>
                <input style={s.input} type="date" value={closeForm.exitDate}
                  onChange={e => setCloseForm(p => ({ ...p, exitDate: e.target.value }))} />
              </div>
              <div>
                <label style={s.label}>Exit Price ($)</label>
                <input style={s.input} type="number" step="0.01" value={closeForm.exitPrice}
                  onChange={e => setCloseForm(p => ({ ...p, exitPrice: e.target.value }))} />
              </div>
              <div>
                <label style={s.label}>Notes</label>
                <input style={s.input} value={closeForm.notes}
                  onChange={e => setCloseForm(p => ({ ...p, notes: e.target.value }))} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => handleClose(closeTarget._id)} disabled={saving} style={{
                ...s.btn, background: '#f59e0b', color: '#0f1923', opacity: saving ? 0.6 : 1
              }}>
                {saving ? 'Saving…' : 'Close Trade'}
              </button>
              <button onClick={() => setCloseTarget(null)} style={{
                ...s.btn, background: '#1e2d3d', color: '#94a3b8'
              }}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Trades table */}
        {trades.length === 0 ? (
          <div style={{ ...s.card, textAlign: 'center', padding: '40px', color: '#475569' }}>
            No signal actions logged yet. Click "+ Log Signal" to start tracking.
          </div>
        ) : (
          <div style={{ ...s.card, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['Ticker','Signal','Score','Entry Date','Entry $','Exit $','Outcome','Status','Notes',''].map(h => (
                    <th key={h} style={s.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {trades.map(trade => {
                  const sigColor = SIGNAL_COLORS[trade.signal] || '#64748b';
                  const isPos = trade.outcome > 0;
                  return (
                    <tr key={trade._id}>
                      <td style={{ ...s.td, fontWeight: 700 }}>{trade.ticker}</td>
                      <td style={{ ...s.td, color: sigColor, fontWeight: 600 }}>{trade.signal}</td>
                      <td style={s.td}>{trade.score ?? '—'}</td>
                      <td style={{ ...s.td, color: '#94a3b8' }}>
                        {trade.entryDate ? new Date(trade.entryDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                      </td>
                      <td style={s.td}>{trade.entryPrice ? '$' + trade.entryPrice : '—'}</td>
                      <td style={s.td}>{trade.exitPrice  ? '$' + trade.exitPrice  : '—'}</td>
                      <td style={{ ...s.td, color: trade.outcome != null ? (isPos ? '#00ff88' : '#ef4444') : '#64748b', fontWeight: 600 }}>
                        {trade.outcome != null ? (isPos ? '+' : '') + trade.outcome + '%' : '—'}
                      </td>
                      <td style={s.td}>
                        <span style={{
                          background: trade.status === 'open' ? '#00d4ff22' : '#1e2d3d',
                          border: `1px solid ${trade.status === 'open' ? '#00d4ff44' : '#2d4060'}`,
                          borderRadius: 6, padding: '2px 8px', fontSize: 11,
                          color: trade.status === 'open' ? '#00d4ff' : '#64748b'
                        }}>
                          {trade.status}
                        </span>
                      </td>
                      <td style={{ ...s.td, color: '#64748b', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {trade.notes || '—'}
                      </td>
                      <td style={s.td}>
                        <div style={{ display: 'flex', gap: 6 }}>
                          {trade.status === 'open' && (
                            <button onClick={() => { setCloseTarget(trade); setCloseForm({ exitDate: '', exitPrice: '', notes: trade.notes || '' }); }}
                              style={{ ...s.btn, padding: '4px 10px', fontSize: 11, background: '#f59e0b22',
                                border: '1px solid #f59e0b44', color: '#f59e0b' }}>
                              Close
                            </button>
                          )}
                          <button onClick={() => handleDelete(trade._id)}
                            style={{ ...s.btn, padding: '4px 10px', fontSize: 11, background: '#ef444422',
                              border: '1px solid #ef444444', color: '#ef4444' }}>
                            ✕
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ marginTop: 20, fontSize: 11, color: '#334155', textAlign: 'center' }}>
          Personal trade journal · Super Admin only · Not investment advice · Past performance does not guarantee future results
        </div>
      </div>
    </div>
  );
}
